import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { apiClient } from '@/lib/api/client'
import { betterAuthClient } from '@/lib/auth/better-auth-client'
import type { SignInEmailPreview } from '@/lib/api/types'
import { XtellarMark } from '@/src/components/XtellarMark'

function Shell({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-md rounded-lg">
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <XtellarMark />
            <span className="text-sm font-semibold uppercase tracking-[0.14em]">Helm</span>
          </div>
          <CardTitle className="text-center">
            <h1 className="text-2xl font-semibold">{title}</h1>
          </CardTitle>
          {description && <CardDescription className="text-center">{description}</CardDescription>}
        </CardHeader>
        {children}
      </Card>
    </main>
  )
}

const goToHelm = (
  <CardFooter className="justify-center">
    <Button variant="outline" asChild>
      <Link to="/">Go to Helm</Link>
    </Button>
  </CardFooter>
)

/**
 * Where the link in a "confirm your new sign-in email" message lands. It asks
 * before using the link: mail scanners open links on their own, and the link
 * works once. Works signed in or not: holding the link proves the inbox.
 */
export function ConfirmSignInEmailPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')?.trim() ?? ''
  const [preview, setPreview] = useState<SignInEmailPreview | null>(null)
  const [loadError, setLoadError] = useState<string | null>(
    token ? null : 'This link is incomplete. Open it again from the email.',
  )
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<{ email: string } | null>(null)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    apiClient
      .previewSignInEmailChange(token)
      .then((result) => !cancelled && setPreview(result))
      .catch(
        (cause) =>
          !cancelled &&
          setLoadError(cause instanceof Error ? cause.message : 'This link is not valid'),
      )
    return () => {
      cancelled = true
    }
  }, [token])

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      const result = await apiClient.confirmSignInEmailChange(token)
      setDone({ email: result.email })
      // A session in this browser still has the old address cached; refresh it.
      void betterAuthClient
        .getSession({ query: { disableCookieCache: true } })
        .catch(() => undefined)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The change could not be confirmed')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <Shell title="Sign-in email changed">
        <CardContent>
          <Alert role="status">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>
              You now sign in to Helm with <strong>{done.email}</strong>. We let your old address
              know about the change.
            </AlertDescription>
          </Alert>
        </CardContent>
        {goToHelm}
      </Shell>
    )
  }

  if (loadError) {
    return (
      <Shell title="This link can't be used">
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
          <p className="mt-4 text-sm text-muted-foreground">
            Nothing has changed. You can ask for a new link from your settings in Helm.
          </p>
        </CardContent>
        {goToHelm}
      </Shell>
    )
  }

  if (!preview) {
    return (
      <Shell title="Checking your link">
        <CardContent className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading" />
        </CardContent>
      </Shell>
    )
  }

  return (
    <Shell
      title="Confirm your new sign-in email"
      description="Once you confirm, this is the address you sign in to Helm with."
    >
      <CardContent className="space-y-4">
        <div className="rounded-md border bg-surface-subtle p-4 text-center">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            New sign-in email
          </div>
          <div className="mt-1 break-all font-medium">{preview.new_email}</div>
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-2">
        <Button className="w-full" onClick={confirm} disabled={busy}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? 'Confirming' : 'Confirm new email'}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          Not expecting this? Close this page and nothing will change.
        </p>
      </CardFooter>
    </Shell>
  )
}
